/** Which tree paths a person may change or read (specs 129, 130). Shared by the assistant runner and the Next.js app (`src/versions/editScope.ts`). */
import picomatch from 'picomatch/posix';

/** These match only when a grant lists them by name, so a glob like `*.json` never reaches them. */
const NAMED_ONLY = new Set(['manifest.json', 'package.json']);

/** The folders `knowledge:write` covers. Every other tree path is code, covered by `code:write`. */
export const KNOWLEDGE_GLOBS = ['prompts/**', 'knowledge/**'] as const;

/**
 * What a person may change in the version tree. `knowledge` lists the globs inside `KNOWLEDGE_GLOBS` they may
 * change, null for all of them, `[]` for none. Without `code`, a subagent file's frontmatter stays as it is.
 * Null or absent where a `WriteScope` is expected means no limit.
 */
export interface WriteScope {
	code: boolean;
	knowledge: string[] | null;
}

/** `./knowledge/../manifest.json` becomes `manifest.json`. Null for a path that is absolute or climbs out of the tree. */
export function normalizeTreePath(path: string): string | null {
	if (!path || path.startsWith('/') || path.includes('\\') || path.includes('\0')) return null;
	const parts: string[] = [];
	for (const part of path.split('/')) {
		if (part === '' || part === '.') continue;
		if (part === '..') {
			if (parts.length === 0) return null;
			parts.pop();
		} else parts.push(part);
	}
	return parts.length > 0 ? parts.join('/') : null;
}

/** Whether a person with these `editPaths` may change, add or delete `path`. `null` means no limit. */
export function canEditPath(editPaths: readonly string[] | null | undefined, path: string): boolean {
	if (editPaths == null) return true;
	const target = normalizeTreePath(path);
	if (!target) return false;
	const patterns = editPaths.map(normalizeTreePath).filter((p): p is string => p !== null);
	if (NAMED_ONLY.has(target)) return patterns.includes(target);
	return patterns.length > 0 && picomatch(patterns, { dot: true })(target);
}

export function isKnowledgePath(path: string): boolean {
	return canEditPath(KNOWLEDGE_GLOBS, path);
}

/** Whether a glob can only match inside `KNOWLEDGE_GLOBS`, so a `knowledge:write` grant may carry it. */
export function isKnowledgePattern(pattern: string): boolean {
	const normalized = normalizeTreePath(pattern);
	return (
		!!normalized &&
		/^(prompts|knowledge)\/./.test(normalized) &&
		!/^(prompts|knowledge)\/\.\./.test(normalized)
	);
}

/** Whether `scope` lets a person change some part of `path`. For a subagent file, `writeRefusal` says which part. */
export function canWritePath(scope: WriteScope | null | undefined, path: string): boolean {
	if (!scope) return true;
	if (!normalizeTreePath(path)) return false;
	if (!isKnowledgePath(path)) return scope.code;
	return canEditPath(scope.knowledge, path) || (scope.code && isSubagentFile(path));
}

/** A subagent file, whose YAML frontmatter is its config (model, tools, triggers). */
export function isSubagentFile(path: string): boolean {
	return /^prompts\/[^/]+\.md$/.test(normalizeTreePath(path) ?? '');
}

const FRONTMATTER = /^---\r?\n([\s\S]*?)\r?\n---[ \t]*(\r?\n|$)/;

/** The file split at its frontmatter, with line endings and trailing spaces evened out so only real changes differ. */
export function splitFrontmatter(text: string | null | undefined): { frontmatter: string; body: string } {
	const m = text ? FRONTMATTER.exec(text) : null;
	const even = (s: string) => s.replace(/\r\n/g, '\n').replace(/[ \t]+$/gm, '');
	if (!m) return { frontmatter: '', body: even(text ?? '') };
	return { frontmatter: even(m[1]!).trim(), body: even(text!.slice(m[0].length)) };
}

export type WriteRefusal = 'outside' | 'frontmatter' | 'body';

/**
 * Why `scope` refuses changing `path` from `before` to `after` (null for a missing file), or null when it may.
 * In a subagent file the frontmatter needs `code` and the body needs the path in `scope.knowledge`.
 */
export function writeRefusal(
	scope: WriteScope | null | undefined,
	path: string,
	before: string | null | undefined,
	after: string | null | undefined,
): WriteRefusal | null {
	if (!scope) return null;
	if (!isSubagentFile(path)) return canWritePath(scope, path) ? null : 'outside';
	const a = splitFrontmatter(before);
	const b = splitFrontmatter(after);
	if (a.frontmatter !== b.frontmatter && !scope.code) return 'frontmatter';
	if (a.body !== b.body && !canEditPath(scope.knowledge, path)) return scope.code ? 'body' : 'outside';
	return null;
}

/** The sentence a refused write shows. */
export function writeRefusalMessage(refusal: WriteRefusal, path: string, scope: WriteScope): string {
	if (refusal === 'frontmatter')
		return `You can change only the text of ${path}, not its frontmatter (model, tools, triggers). That needs the "Edit agent code" permission.`;
	if (refusal === 'body') return `You can change only the frontmatter of ${path}, not its text.`;
	return `You can only edit ${describeWriteScope(scope)}. ${path} is outside that.`;
}

/** The scope in words, like "prompts/sales.md" or "everything except prompts and knowledge". */
export function describeWriteScope(scope: WriteScope): string {
	const knowledge = scope.knowledge === null ? 'prompts and knowledge' : scope.knowledge.join(', ');
	if (scope.code)
		return scope.knowledge?.length === 0
			? 'code and subagent settings, not prompts or knowledge'
			: `code and ${knowledge}`;
	return scope.knowledge?.length === 0 ? 'no files' : `the text of ${knowledge}`;
}

/** Whether a person whose reads are limited to `readPaths` may see `path`. `null` means no limit. */
export function canReadPath(readPaths: readonly string[] | null | undefined, path: string): boolean {
	return canEditPath(readPaths, path);
}

/** Whether a folder could hold a file `readPaths` allows, so a listing may show it. */
export function canReadUnder(readPaths: readonly string[] | null | undefined, dir: string): boolean {
	if (readPaths == null) return true;
	const target = normalizeTreePath(dir);
	if (!target) return true;
	return readPaths.some((pattern) => {
		const base = picomatch.scan(normalizeTreePath(pattern) ?? '').base;
		return !base || base === target || base.startsWith(`${target}/`) || target.startsWith(`${base}/`);
	});
}
